import React, { useState } from 'react';
import './styles/EmployeeManual.css';
import ThemeToggle from './styles/ThemeToggle';

const t = {
  en: {
    title: 'DiNi Wigs — Employee Manual',
    subtitle: 'Policies & Guidelines',
    effective: 'Effective: May 2026  |  For Internal Use Only',
    translateBtn: 'Traducir al Español',

    // Holiday section
    holidayTitle: 'Holiday & Time-Off Policy',
    holidayIntro:
      'DiNi Wigs observes the following company holidays. On these days the store is closed and employees are not required to use a personal day. Employees are encouraged to plan vacations around these holidays to take full advantage of the time off.',

    closedTitle: 'Company Holidays — Store Closed (No Personal Day Required)',
    closedNote:
      'Planning vacations around these holidays is strongly encouraged so you can enjoy more time off without touching your personal day balance.',

    federalTitle: 'US Federal Holidays — Store Remains Open',
    federalNote:
      'The store remains open on all other US Federal Holidays. Employees may request the day off, but it will count as one of their 5 personal days for the year.',

    scheduleTitle: '2026 Holiday Schedule',
    colHoliday: 'Holiday',
    colDate: '2026 Date',
    colStatus: 'Store Status',
    colPersonal: 'Personal Day Used?',
    closed: 'Closed',
    open: 'Open',
    no: 'No',
    yesIfTaken: 'Yes — if taken',
    tbd: 'TBD',
    shavuot: 'Shavuot',
    roshHashana: 'Rosh Hashana',
    pesach: 'Pesach (Passover)',
    sukkot: 'Sukkot',
    thanksgiving: 'Thanksgiving',
    christmas: 'Christmas',
    federalOther: 'All Other US Federal Holidays',

    // PTO section
    ptoTitle: 'Paid Time Off (PTO) & Sick Day Policy',

    ptoSubTitle: 'PTO — Personal & Vacation Days',
    ptoBullets: [
      'Each employee receives 5 paid personal/vacation days per year.',
      'The PTO year runs from Pesach to the following Pesach (approximately April to April).',
      'PTO is paid out as part of the annual bonus check, issued two weeks after the end of each Pesach.',
      'Thanksgiving and Christmas do not count toward the 5 personal days (store remains open on both, employees may work on these days if they choose).',
    ],

    sickSubTitle: 'Paid Sick Days',
    sickBullets: [
      'Each employee receives 5 paid sick days per year.',
      'The sick-day year runs from Sukkot to the following Sukkot (approximately October to October).',
      'Sick day eligibility follows state legal guidelines for employees working 30 or more hours per week, accrued from the date of hire.',
      'Unused sick days carry over to the next sick-day year as required by NY Labor Law § 196-b. Employees may voluntarily request a sick day cash-out at year end; no automatic cash-out will occur.',
      'The annual usage cap is 5 paid sick days per sick-day year. Carried-over days remain in your balance but do not increase the yearly usage maximum.',
      'Hospitalizations supported by proper medical documentation will not count toward your sick day balance.',
    ],

    deductTitle: 'Beyond Your Allocated Days',
    deductBullets: [
      'Absences beyond your 5 allocated paid sick days per sick-day year will be unpaid. Unused paid sick days carry over — they are not forfeited.',
      'Absences beyond your 5 allocated personal days per PTO year will be unpaid.',
    ],

    // Extended Illness / DBL
    dblTitle: 'Extended Illness & Hospitalization Policy',
    dblIntro: 'If an employee experiences a serious medical emergency, major surgery, or an inpatient hospital admission requiring an extended absence, the Company does not provide continuous out-of-pocket salary continuation. Instead, these absences are handled through New York State Short-Term Disability Benefits Law (DBL) insurance.',
    dblItems: [
      { heading: 'Sick Day Protection', text: 'Days spent in the hospital or on approved short-term disability are treated as excused medical leave and will not be deducted from the employee\'s standard annual 5-day Paid Sick Leave balance.' },
      { heading: 'The 7-Day Waiting Period', text: 'NY State disability insurance requires a standard 7-calendar-day waiting period before insurance payouts begin. To maintain income during this first week, employees may voluntarily use any available accrued Paid Sick Leave or Personal/Vacation Days. If no company time-off balances are available — or if the employee chooses not to use them — the first 7 days will be taken as unpaid, job-protected medical leave.' },
      { heading: 'Benefit Amount', text: 'Benefits begin on the 8th consecutive day of an off-the-job illness or injury, paying 50% of the employee\'s average weekly wage up to the state-mandated maximum.' },
      { heading: 'Filing a Claim', text: 'Employees must submit formal medical documentation directly to the company\'s insurance provider to initiate a claim. NY State DBL requires employees to complete Form DB-450, a three-part document submitted to the employer\'s insurance carrier after the 7-day waiting period.' },
    ],
    dblFormNote: 'The official DB-450 form can be accessed at the New York State Workers\' Compensation Board:',
    dblFormUrl: 'https://www.wcb.ny.gov/content/main/DisabilityBenefits/employee-disability-benefits.jsp',

    // NY Law Reference
    nyLawTitle: 'NY State Law Reference — Sick Days & Personal Days',
    nyLawIntro: 'We want to be transparent about how New York State law defines and governs both sick leave and personal days. Understanding the legal framework helps everyone know their rights and our responsibilities as an employer.',

    sickLawTitle: 'Sick Days — New York Labor Law § 196-b',
    sickLawIntro: 'Paid sick leave in New York State is a mandatory benefit governed by NY Labor Law § 196-b. Here is exactly what the law requires and what qualifies:',
    sickLawItems: [
      { heading: 'Physical & Mental Health', text: 'NY Labor Law § 196-b(4)(a)(i)–(ii) requires employers to allow sick leave for any mental or physical illness, injury, or health condition, including medical appointments and preventative care — regardless of whether a diagnosis has been made at the time of the request.' },
      { heading: 'Family Care', text: 'The same statute mandates that sick leave extends to caring for a family member: child, spouse, domestic partner, parent, sibling, grandchild, or grandparent.' },
      { heading: 'Safe Leave', text: 'NY Labor Law § 196-b(4)(a)(iii) also covers absences when you or a family member has been a victim of domestic violence, a family offense, sexual offense, stalking, or human trafficking — to access shelter, legal counsel, or to relocate.' },
      { heading: 'Legal Protections', text: 'The NYSDOL explicitly prohibits retaliation. We cannot discharge, threaten, penalize, or discipline any employee for exercising rights under this law. We also cannot require medical documentation unless you are absent for more than three consecutive scheduled workdays.' },
    ],
    sickPayNote: 'Sick day pay for days used is processed on the following pay cycle. Unused sick days carry over to the following year per NY state law — they are not automatically cashed out. Employees who wish to voluntarily cash out unused sick days at year end may request to do so.',
    sickLawSource: 'Source: NY Labor Law § 196-b — nysenate.gov/legislation/laws/LAB/196-B',
    sickLawSourceUrl: 'https://www.nysenate.gov/legislation/laws/LAB/196-B',

    personalLawTitle: 'Personal Days — No State Mandate',
    personalLawIntro: 'Unlike sick leave, personal days are not required by New York State law. This is an important distinction every employee should understand:',
    personalLawItems: [
      { heading: 'Not Mandated by NY State', text: 'The New York State Attorney General\'s Office confirms that while New York mandates sick leave rules, there is no state law requiring private employers to provide general paid time off, vacation days, or personal days.' },
      { heading: 'Company Discretion', text: 'Because personal days are a voluntary benefit we offer — not a legal entitlement — they are governed by our internal company policy and standard contract law. We retain full discretion over when and how personal days are approved.' },
      { heading: 'Approval & Denial', text: 'Per NYSDOL Employee Benefits Guidelines, management may legally deny a personal day request based on business or operational needs. Personal days are not a protected right the way sick leave is.' },
      { heading: 'What Qualifies', text: 'Because there is no legal definition of a "personal day," HR compliance standards classify these as discretionary absences for personal matters that do not meet the medical, family care, or safe leave criteria defined in NY Labor Law § 196-b — things like errands, travel, or general rest.' },
    ],
    nyLawClosing: 'We are committed to honoring every right guaranteed to you under New York State law. At the same time, we ask all employees to respect the distinction between legally protected sick leave and the discretionary personal days we provide as an additional benefit.',

    // Paid Family Leave
    pflTitle: 'Maternity & Family Leave — New York State Paid Family Leave',
    pflBody1:
      'DiNi Wigs employees are covered under New York State Paid Family Leave (NYS PFL). This is a state-funded benefit — the cost is covered through employee payroll deductions, not by the company.',
    pflBody2:
      'NYS PFL provides up to 12 weeks of paid, job-protected leave to bond with a newly born, adopted, or fostered child, or to care for a seriously ill family member.',
    pflNote: 'To apply, employees must file directly with the state. For more information and to begin your application, visit the official NYS PFL website:',
    pflLink: 'https://paidfamilyleave.ny.gov',

    // Work hours
    workHoursTitle: 'Work Hours Policy',
    workHoursBody1:
      'Our schedule is 40 hours per week (plus 2.5 hours for lunch).',
    workHoursBody2:
      'Every worker is responsible for stopping work after those hours. Under the FLSA and NY State law, all hours worked beyond 40 in a workweek will be compensated at 1.5× the regular rate regardless of prior approval. However, working unauthorized overtime is a violation of company policy and may result in disciplinary action up to and including termination.',

    // Cell phone
    cellTitle: 'Cell Phone Policy',
    cellBody1:
      'We have noticed that the policy regarding cell phone use during work hours is not being consistently followed. As a reminder, personal cell phone use is strictly prohibited during work hours, unless it is for a true emergency.',
    cellBody2:
      'Phones must be kept in your pocket or placed at the designated charging station — not on your desk or workstation.',
    cellBody3:
      'This policy is in place to maintain focus and efficiency throughout the day. We ask for your full cooperation moving forward.',

    // HR message
    hrTitle: 'Dress Code Policy',
    hrIntro:
      'As part of maintaining the professional and polished environment of our luxury brand, we ask all employees to please come dressed in casual-professional and work-appropriate attire.',
    hrBullets: [
      'Please avoid clothing that is overly revealing or too casual.',
      'Shorts and sleeveless tops are not permitted.',
      'Shirts should be an appropriate length and neckline.',
      'Skirts and dresses should be a professional length appropriate for the workplace.',
    ],
    hrHelp:
      'We understand that finding appropriate work attire can sometimes be challenging, and we are happy to help. If anyone needs, we can gladly provide DiNi Wigs shirts for staff to wear during work hours.',
    hrClose:
      'We truly appreciate everyone\'s cooperation and professionalism in helping us maintain the elevated image and standard of our company. Thank you so much.',

    // Contacts
    contactsTitle: 'Contacts',
    contactsNote: 'Best reached via WhatsApp.',
    contacts: [
      { role: 'HR',              name: 'Gitty Medal',   phone: '+1 (646) 256-7097' },
      { role: 'General Manager', name: 'Didi Puentes',  phone: '+1 (845) 405-6034' },
    ],

    tocTitle: 'Table of Contents',

    // Bereavement
    bereaveTitle: 'Bereavement Leave',
    bereaveBody: 'DiNi Wigs understands that the loss of a loved one is a profoundly difficult time. Bereavement leave is handled on a case-by-case basis. Employees who experience the loss of an immediate family member are encouraged to notify HR or the General Manager as soon as possible to discuss available arrangements.',

    // Jury Duty
    juryTitle: 'Jury Duty',
    juryBody1: 'DiNi Wigs supports all employees summoned for jury duty. Under New York Labor Law § 519, employees have the right to take time off to serve on a jury without risk of job loss or retaliation.',
    juryBody2: 'NY Labor Law § 519 requires employers to pay employees their regular wages for the first three days of jury service. Employees must notify management as soon as they receive a jury summons and provide HR with a copy.',
    jurySource: 'Source: NY Labor Law § 519',
    jurySourceUrl: 'https://www.nysenate.gov/legislation/laws/LAB/519',

    // Pay Schedule
    payTitle: 'Pay Schedule & Paystubs',
    payBody1: 'Employees are paid on a weekly basis. The pay week runs from Thursday to Thursday.',
    payBody2: 'Paystubs are available online through Viventium. You can view and download your paystub at any time by logging in to your account.',
    payStubNote: 'Access your paystub at:',
    payStubUrl: 'https://viventium.com/',
    payHrNote: 'For new account sign-up or technical support logging in, please contact HR.',

    // Anti-Harassment
    antiHarassTitle: 'Anti-Harassment & Non-Discrimination Policy',
    antiHarassBody: 'DiNi Wigs is committed to maintaining a workplace free from harassment, discrimination, and retaliation of any kind. Under the New York State Human Rights Law (Executive Law Article 15) — which provides broader protections than federal law — discrimination or harassment based on race, color, religion, sex, national origin, age, disability, sexual orientation, gender identity or expression, marital status, or any other protected characteristic is strictly prohibited.',
    antiHarassBullets: [
      'All employees are expected to treat coworkers, clients, and management with dignity and respect at all times.',
      'Any employee who experiences or witnesses harassment or discrimination must report it to HR immediately.',
      'All reports will be investigated promptly and kept as confidential as possible. Retaliation against any employee who makes a good-faith report is strictly prohibited and will result in disciplinary action up to and including termination.',
    ],
    antiHarassSource: 'Source: NY State Human Rights Law (Executive Law Article 15)',
    antiHarassSourceUrl: 'https://dhr.ny.gov',

    footer: '© 2026 DiNi Wigs LLC. All rights reserved.',
  },

  es: {
    title: 'DiNi Wigs — Manual del Empleado',
    subtitle: 'Políticas y Pautas',
    effective: 'Vigente: Mayo 2026  |  Solo para uso interno',
    translateBtn: 'Translate to English',

    // Holiday section
    holidayTitle: 'Política de Días Festivos y Tiempo Libre',
    holidayIntro:
      'DiNi Wigs observa los siguientes días festivos de la empresa. En estos días la tienda permanece cerrada y los empleados no necesitan usar un día personal. Se alienta a los empleados a planificar sus vacaciones alrededor de estos días festivos para aprovechar al máximo el tiempo libre.',

    closedTitle: 'Días Festivos de la Empresa — Tienda Cerrada (No se Requiere Día Personal)',
    closedNote:
      'Se recomienda encarecidamente planificar las vacaciones alrededor de estos días para disfrutar de más tiempo libre sin afectar el saldo de días personales.',

    federalTitle: 'Días Festivos Federales de EE. UU. — La Tienda Permanece Abierta',
    federalNote:
      'La tienda permanece abierta en todos los demás días festivos federales de EE. UU. Los empleados pueden solicitar el día libre, pero contará como uno de sus 5 días personales del año.',

    scheduleTitle: 'Calendario de Días Festivos 2026',
    colHoliday: 'Día Festivo',
    colDate: 'Fecha 2026',
    colStatus: 'Estado de la Tienda',
    colPersonal: '¿Se Usa Día Personal?',
    closed: 'Cerrada',
    open: 'Abierta',
    no: 'No',
    yesIfTaken: 'Sí — si se toma',
    tbd: 'Por definir',
    shavuot: 'Shavuot',
    roshHashana: 'Rosh Hashaná',
    pesach: 'Pésaj (Pascua Judía)',
    sukkot: 'Sucot',
    thanksgiving: 'Día de Acción de Gracias',
    christmas: 'Navidad',
    federalOther: 'Todos los demás días festivos federales de EE. UU.',

    // PTO section
    ptoTitle: 'Política de Tiempo Libre Pagado (PTO) y Días de Enfermedad',

    ptoSubTitle: 'PTO — Días Personales y de Vacaciones',
    ptoBullets: [
      'Cada empleado recibe 5 días personales/de vacaciones pagados por año.',
      'El año de PTO va desde Pésaj hasta el siguiente Pésaj (aproximadamente de abril a abril).',
      'El PTO se paga como parte del bono anual, emitido dos semanas después del final de Pésaj.',
      'El Día de Acción de Gracias y la Navidad no cuentan como días personales (la tienda permanece abierta en ambos días; los empleados pueden trabajar esos días si así lo desean).',
    ],

    sickSubTitle: 'Días de Enfermedad Pagados',
    sickBullets: [
      'Cada empleado recibe 5 días de enfermedad pagados por año.',
      'El año de días de enfermedad va desde Sucot hasta el siguiente Sucot (aproximadamente de octubre a octubre).',
      'La elegibilidad sigue las pautas legales estatales para empleados que trabajan 30 o más horas por semana, acumulados desde la fecha de contratación.',
      'Los días de enfermedad no utilizados se transfieren al siguiente año de acuerdo con la Ley Laboral de NY § 196-b — no se cobran automáticamente. Los empleados que deseen canjear voluntariamente días de enfermedad no utilizados al final del año pueden solicitarlo.',
      'El límite anual de uso es de 5 días de enfermedad pagados por año. Los días acumulados permanecen en su saldo, pero no aumentan el máximo anual de uso.',
      'Las hospitalizaciones con documentación médica adecuada no contarán para el saldo de días de enfermedad.',
    ],

    deductTitle: 'Más Allá de los Días Asignados',
    deductBullets: [
      'Las ausencias que superen los 5 días de enfermedad pagados asignados por año serán sin pago. Los días de enfermedad no utilizados se transfieren — no se pierden.',
      'Las ausencias que superen los 5 días personales asignados por año de PTO serán sin pago.',
    ],

    // Extended Illness / DBL
    dblTitle: 'Política de Enfermedad Prolongada y Hospitalización',
    dblIntro: 'Si un empleado experimenta una emergencia médica grave, una cirugía mayor o una hospitalización que requiere una ausencia prolongada, la Empresa no proporciona continuación de salario de su propio bolsillo. En cambio, estas ausencias se manejan a través del seguro de la Ley de Beneficios por Discapacidad a Corto Plazo del Estado de Nueva York (DBL).',
    dblItems: [
      { heading: 'Protección de Días de Enfermedad', text: 'Los días pasados en el hospital o en una discapacidad a corto plazo aprobada se tratan como licencia médica justificada y no se deducirán del saldo estándar anual de 5 días de Licencia por Enfermedad Pagada del empleado.' },
      { heading: 'El Período de Espera de 7 Días', text: 'El seguro de discapacidad del Estado de NY requiere un período de espera estándar de 7 días calendario antes de que comiencen los pagos del seguro. Para mantener ingresos durante esta primera semana, los empleados pueden usar voluntariamente cualquier Licencia por Enfermedad Pagada o Días Personales/Vacaciones disponibles. Si no hay saldos disponibles — o si el empleado elige no usarlos — los primeros 7 días serán licencia médica no remunerada y protegida por el trabajo.' },
      { heading: 'Monto del Beneficio', text: 'Los beneficios comienzan el 8.° día consecutivo de una enfermedad o lesión fuera del trabajo, pagando el 50% del salario semanal promedio del empleado hasta el máximo establecido por el estado.' },
      { heading: 'Presentar un Reclamo', text: 'Los empleados deben presentar documentación médica formal directamente al proveedor de seguros de la empresa. El DBL de NY requiere que los empleados completen el Formulario DB-450, un documento de tres partes presentado al portador de seguros del empleador después del período de espera de 7 días.' },
    ],
    dblFormNote: 'El formulario oficial DB-450 se puede acceder en la Junta de Compensación para Trabajadores del Estado de Nueva York:',
    dblFormUrl: 'https://www.wcb.ny.gov/content/main/DisabilityBenefits/employee-disability-benefits.jsp',

    // NY Law Reference
    nyLawTitle: 'Referencia Legal de NY — Días de Enfermedad y Días Personales',
    nyLawIntro: 'Queremos ser transparentes sobre cómo la ley del Estado de Nueva York define y regula tanto la licencia por enfermedad como los días personales. Comprender el marco legal ayuda a todos a conocer sus derechos y nuestras responsabilidades como empleador.',

    sickLawTitle: 'Días de Enfermedad — Ley Laboral de Nueva York § 196-b',
    sickLawIntro: 'La licencia por enfermedad pagada en el Estado de Nueva York es un beneficio obligatorio regulado por la Ley Laboral de NY § 196-b. Esto es exactamente lo que exige la ley y qué califica:',
    sickLawItems: [
      { heading: 'Salud Física y Mental', text: 'La Ley Laboral de NY § 196-b(4)(a)(i)–(ii) requiere que los empleadores permitan licencia por enfermedad para cualquier enfermedad mental o física, lesión o condición de salud, incluyendo citas médicas y cuidado preventivo — independientemente de si se ha realizado un diagnóstico al momento de la solicitud.' },
      { heading: 'Cuidado Familiar', text: 'El mismo estatuto exige que la licencia por enfermedad se extienda al cuidado de un familiar: hijo/a, cónyuge, pareja doméstica, padre/madre, hermano/a, nieto/a o abuelo/a.' },
      { heading: 'Licencia de Seguridad', text: 'La Ley Laboral de NY § 196-b(4)(a)(iii) también cubre ausencias cuando usted o un familiar ha sido víctima de violencia doméstica, una ofensa familiar, ofensa sexual, acecho o tráfico humano — para acceder a refugio, asesoramiento legal o reubicarse.' },
      { heading: 'Protecciones Legales', text: 'El NYSDOL prohíbe explícitamente las represalias. No podemos despedir, amenazar, penalizar o disciplinar a ningún empleado por ejercer sus derechos bajo esta ley. Tampoco podemos exigir documentación médica a menos que la ausencia sea de más de tres días laborales consecutivos.' },
    ],
    sickPayNote: 'El pago por días de enfermedad utilizados se procesa en el siguiente ciclo de pago. Los días de enfermedad no utilizados se transfieren al año siguiente según la ley estatal — no se cobran automáticamente. Los empleados que deseen canjear voluntariamente días no utilizados al final del año pueden solicitarlo.',
    sickLawSource: 'Fuente: Ley Laboral de NY § 196-b — nysenate.gov/legislation/laws/LAB/196-B',
    sickLawSourceUrl: 'https://www.nysenate.gov/legislation/laws/LAB/196-B',

    personalLawTitle: 'Días Personales — Sin Mandato Estatal',
    personalLawIntro: 'A diferencia de la licencia por enfermedad, los días personales no son requeridos por la ley del Estado de Nueva York. Esta es una distinción importante que todo empleado debe comprender:',
    personalLawItems: [
      { heading: 'No Exigidos por NY', text: 'La Oficina del Fiscal General del Estado de Nueva York confirma que, si bien Nueva York exige reglas de licencia por enfermedad, no existe ninguna ley estatal que requiera que los empleadores privados proporcionen días libres pagados generales, días de vacaciones o días personales.' },
      { heading: 'Discreción de la Empresa', text: 'Debido a que los días personales son un beneficio voluntario que ofrecemos — no un derecho legal — se rigen por nuestra política interna y los principios del derecho contractual estándar. Mantenemos plena discreción sobre cuándo y cómo se aprueban los días personales.' },
      { heading: 'Aprobación y Denegación', text: 'Según las Pautas de Beneficios para Empleados del NYSDOL, la gerencia puede legalmente denegar una solicitud de día personal por razones operativas o de negocio. Los días personales no son un derecho protegido como sí lo es la licencia por enfermedad.' },
      { heading: 'Qué Califica', text: 'Dado que no existe una definición legal de "día personal," los estándares de cumplimiento de recursos humanos los clasifican como ausencias discrecionales para asuntos personales que no cumplen los criterios médicos, de cuidado familiar o de seguridad definidos en la Ley Laboral de NY § 196-b.' },
    ],
    nyLawClosing: 'Estamos comprometidos a respetar cada derecho garantizado por la ley del Estado de Nueva York. Al mismo tiempo, pedimos a todos los empleados que respeten la distinción entre la licencia por enfermedad legalmente protegida y los días personales discrecionales que ofrecemos como beneficio adicional.',

    // Paid Family Leave
    pflTitle: 'Licencia de Maternidad y Familiar — Licencia Familiar Pagada del Estado de Nueva York',
    pflBody1:
      'Los empleados de DiNi Wigs están cubiertos por la Licencia Familiar Pagada del Estado de Nueva York (NYS PFL). Este es un beneficio financiado por el estado — el costo se cubre a través de deducciones de nómina del empleado, no por la empresa.',
    pflBody2:
      'La NYS PFL ofrece hasta 12 semanas de licencia pagada y protegida para vincularse con un recién nacido, adoptado o acogido, o para cuidar a un familiar gravemente enfermo.',
    pflNote: 'Para solicitar, los empleados deben presentar la solicitud directamente al estado. Para más información y para comenzar su solicitud, visite el sitio web oficial de NYS PFL:',
    pflLink: 'https://paidfamilyleave.ny.gov',

    // Work hours
    workHoursTitle: 'Política de Horas de Trabajo',
    workHoursHeading: 'Recordatorio Importante sobre Nuestra Política de Horas de Trabajo',
    workHoursBody1:
      'Nuestro horario es de 40 horas por semana (más 2.5 horas para el almuerzo).',
    workHoursBody2:
      'Cada trabajadora es responsable de dejar de trabajar después de esas horas. Conforme a la FLSA y la ley del Estado de Nueva York, todas las horas trabajadas más allá de 40 en una semana laboral serán compensadas a 1.5× la tarifa regular, independientemente de si fueron aprobadas previamente. Sin embargo, trabajar horas extra no autorizadas es una violación de la política de la empresa y puede resultar en medidas disciplinarias, incluido el despido.',

    // Cell phone
    cellTitle: 'Política de Teléfono Celular',
    cellBody1:
      'Hemos notado que la política sobre el uso del teléfono celular durante las horas de trabajo no se está siguiendo de manera consistente. Como recordatorio, el uso personal del teléfono celular está estrictamente prohibido durante las horas de trabajo, a menos que sea una verdadera emergencia.',
    cellBody2:
      'Los teléfonos deben mantenerse en el bolsillo o colocarse en la estación de carga designada — no sobre el escritorio ni en la estación de trabajo.',
    cellBody3:
      'Esta política está en vigor para mantener el enfoque y la eficiencia durante el día. Pedimos su plena cooperación a partir de ahora.',

    // HR message
    hrTitle: 'Política de Vestimenta',
    hrIntro:
      'Como parte del mantenimiento del ambiente profesional de nuestra marca de lujo, pedimos a todos los empleados que vengan vestidos con ropa casual-profesional y apropiada para el trabajo.',
    hrBullets: [
      'Por favor evite ropa demasiado reveladora o demasiado informal.',
      'No se permiten pantalones cortos ni tops sin mangas.',
      'Las camisas deben tener una longitud y escote apropiados.',
      'Las faldas y vestidos deben tener una longitud profesional adecuada para el lugar de trabajo.',
    ],
    hrHelp:
      'Entendemos que a veces puede ser difícil encontrar ropa de trabajo adecuada, y estamos felices de ayudar. Si alguien lo necesita, con gusto podemos proporcionar camisas de DiNi Wigs para el personal durante las horas de trabajo.',
    hrClose:
      'Realmente apreciamos la cooperación y el profesionalismo de todos para ayudarnos a mantener la imagen y el estándar elevados de nuestra empresa. Muchas gracias.',

    // Contacts
    contactsTitle: 'Contactos',
    contactsNote: 'Se recomienda contactar por WhatsApp.',
    contacts: [
      { role: 'Recursos Humanos',   name: 'Gitty Medal',  phone: '+1 (646) 256-7097' },
      { role: 'Gerente General',    name: 'Didi Puentes', phone: '+1 (845) 405-6034' },
    ],

    tocTitle: 'Tabla de Contenidos',

    // Bereavement
    bereaveTitle: 'Licencia por Duelo',
    bereaveBody: 'DiNi Wigs comprende que la pérdida de un ser querido es un momento profundamente difícil. La licencia por duelo se evalúa caso por caso. Los empleados que experimenten la pérdida de un familiar cercano deben notificar a Recursos Humanos o al Gerente General lo antes posible para hablar sobre las opciones disponibles.',

    // Jury Duty
    juryTitle: 'Deber de Jurado',
    juryBody1: 'DiNi Wigs apoya a todos los empleados convocados para el deber de jurado. De acuerdo con la Ley Laboral de Nueva York § 519, los empleados tienen derecho a tomar tiempo libre para servir en un jurado sin riesgo de perder su trabajo ni represalias.',
    juryBody2: 'La Ley Laboral de NY § 519 requiere que los empleadores paguen a los empleados su salario regular durante los primeros tres días de servicio como jurado. Los empleados deben notificar a la gerencia tan pronto reciban una citación y proporcionar una copia a Recursos Humanos.',
    jurySource: 'Fuente: Ley Laboral de NY § 519',
    jurySourceUrl: 'https://www.nysenate.gov/legislation/laws/LAB/519',

    // Pay Schedule
    payTitle: 'Calendario de Pago y Recibos de Nómina',
    payBody1: 'Los empleados reciben su pago semanalmente. La semana de pago va de jueves a jueves.',
    payBody2: 'Los recibos de nómina están disponibles en línea a través de Viventium. Puede ver y descargar su recibo en cualquier momento iniciando sesión en su cuenta.',
    payStubNote: 'Acceda a su recibo de nómina en:',
    payStubUrl: 'https://viventium.com/',
    payHrNote: 'Para registrarse en una nueva cuenta o para soporte técnico para iniciar sesión, comuníquese con Recursos Humanos.',

    // Anti-Harassment
    antiHarassTitle: 'Política Contra el Acoso y la Discriminación',
    antiHarassBody: 'DiNi Wigs se compromete a mantener un lugar de trabajo libre de acoso, discriminación y represalias de cualquier tipo. Bajo la Ley de Derechos Humanos del Estado de Nueva York (Artículo 15 de la Ley Ejecutiva) — que ofrece protecciones más amplias que la ley federal — la discriminación o el acoso basado en raza, color, religión, sexo, origen nacional, edad, discapacidad, orientación sexual, identidad o expresión de género, estado civil u otra característica protegida está estrictamente prohibido.',
    antiHarassBullets: [
      'Se espera que todos los empleados traten a sus compañeros, clientes y la gerencia con dignidad y respeto en todo momento.',
      'Todo empleado que experimente o presencie acoso o discriminación debe informarlo a Recursos Humanos de inmediato.',
      'Todos los informes serán investigados con prontitud y se mantendrán en la mayor confidencialidad posible. Las represalias contra cualquier empleado que presente un informe de buena fe están estrictamente prohibidas y resultarán en medidas disciplinarias, incluyendo el despido.',
    ],
    antiHarassSource: 'Fuente: Ley de Derechos Humanos del Estado de NY (Artículo 15 de la Ley Ejecutiva)',
    antiHarassSourceUrl: 'https://dhr.ny.gov',

    footer: '© 2026 DiNi Wigs LLC. Todos los derechos reservados.',
  },
};

const closedHolidays = ['shavuot', 'roshHashana', 'pesach', 'sukkot'];

// ── EDIT HOLIDAY DATES HERE ──────────────────────────────────────────────────
  const holidayDates = {
    shavuot:     'May 22, 2026',
    roshHashana: 'Sep 20–22, 2026',
    pesach:      'Apr 1–9, 2026',
    sukkot:      'Sep 25 – Oct 2, 2026',
    thanksgiving:'Nov 26, 2026',
    christmas:   'Dec 25, 2026',
  };
// ─────────────────────────────────────────────────────────────────────────────

const EmployeeManual = () => {
  const [lang, setLang] = useState('en');
  const [showContacts, setShowContacts] = useState(false);
  const [showWorkHours, setShowWorkHours] = useState(false);
  const [showCellPhone, setShowCellPhone] = useState(false);
  const [showDressCode, setShowDressCode] = useState(false);
  const [showHoliday, setShowHoliday] = useState(false);
  const [showPto, setShowPto] = useState(false);
  const [showNyLaw, setShowNyLaw] = useState(false);
  const [showDbl, setShowDbl] = useState(false);
  const [showPfl, setShowPfl] = useState(false);
  const [showBereavement, setShowBereavement] = useState(false);
  const [showJury, setShowJury] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showAntiHarass, setShowAntiHarass] = useState(false);
  const c = t[lang];

  const handlePrintMemo = () => {
    const memo = document.querySelector('.em-memo-sidebar');
    if (!memo) return;

    const win = window.open('', '_blank');
    if (!win) return;

    win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>DiNi Wigs — Internal Memorandum</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; padding: 48px; line-height: 1.6; font-size: 13px; max-width: 720px; margin: 0 auto; }
  h3 { font-size: 18px; font-weight: 700; margin: 0 0 4px; }
  p { margin: 0 0 10px; }
  ul { margin: 6px 0 10px 20px; }
  li { margin-bottom: 6px; }
  strong { font-weight: 700; }
  em { font-style: italic; }
  .em-memo-badge { display: inline-block; background: #c00; color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 1px; padding: 3px 10px; border-radius: 3px; text-transform: uppercase; margin-bottom: 8px; }
  .em-memo-header { border-bottom: 2px solid #c00; padding-bottom: 12px; margin-bottom: 20px; }
  .em-memo-subtitle { font-size: 11px; font-style: italic; color: #555; margin: 0; }
  .em-memo-meta { border: 1px solid #ccc; border-radius: 4px; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; }
  .em-memo-meta-row { margin-bottom: 4px; }
  .em-memo-meta-row span { font-weight: 700; display: inline-block; width: 50px; }
  .em-memo-alert { background: #fff3cd; border: 1px solid #f59e0b; border-radius: 4px; padding: 8px 12px; margin-bottom: 14px; font-size: 13px; }
  @page { margin: 1.5cm; }
</style>
</head>
<body>${memo.innerHTML}</body>
</html>`);
    win.document.close();
    win.focus();
    win.print();
    win.addEventListener('afterprint', () => win.close());
  };

  const handlePrint = () => {
    setShowContacts(true);
    setShowWorkHours(true);
    setShowCellPhone(true);
    setShowDressCode(true);
    setShowHoliday(true);
    setShowPto(true);
    setShowNyLaw(true);
    setShowDbl(true);
    setShowPfl(true);
    setShowBereavement(true);
    setShowJury(true);
    setShowPay(true);
    setShowAntiHarass(true);

    setTimeout(() => {
      const container = document.querySelector('.em-container');
      if (!container) return;

      const win = window.open('', '_blank');
      if (!win) { window.print(); return; }

      win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>DiNi Wigs — Employee Manual</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; padding: 32px 48px; line-height: 1.6; font-size: 13px; }
  button, nav, .em-chevron { display: none !important; }
  h1 { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
  h2 { font-size: 16px; font-weight: 600; border-left: 3px solid #555; padding-left: 10px; margin: 24px 0 10px; }
  h3 { font-size: 14px; font-weight: 600; margin: 14px 0 6px; }
  p { margin: 0 0 10px; }
  ul { margin: 6px 0 10px 20px; }
  li { margin-bottom: 4px; }
  a { color: #000; text-decoration: underline; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  th, td { border: 1px solid #aaa; padding: 6px 10px; text-align: left; }
  th { background: #eee; font-weight: 700; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .em-section { margin-bottom: 28px; padding-bottom: 16px; border-bottom: 1px solid #ddd; }
  .em-collapsible-body, .em-collapsible-title { display: block !important; }
  .em-hr-box, .em-ceo-box, .em-info-box { border: 1px solid #ccc; border-radius: 4px; padding: 12px 16px; margin-bottom: 12px; }
  .em-badge { border: 1px solid #888; border-radius: 3px; padding: 1px 6px; font-size: 11px; font-weight: 600; }
  .em-section-title::before { color: #555; }
  @page { margin: 1.5cm; }
</style>
</head>
<body>${container.innerHTML}</body>
</html>`);
      win.document.close();
      win.focus();
      win.print();
      win.addEventListener('afterprint', () => win.close());
    }, 400);
  };

  return (
    <div className="em-page">
      <div className="em-layout">
      <div className="em-container">

        {/* Header */}
        <div className="em-header">
          <div>
            <h1 className="em-title">{c.title}</h1>
            <p className="em-subtitle">{c.subtitle}</p>
            <p className="em-effective">{c.effective}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <ThemeToggle />
            <button className="em-print-btn" onClick={handlePrint}>Print / Save PDF</button>
            <button
              className="em-translate-btn"
              onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
            >
              {c.translateBtn}
            </button>
          </div>
        </div>

        {/* ── TABLE OF CONTENTS ── */}
        <nav className="em-toc">
          <p className="em-toc-title">{c.tocTitle}</p>
          <ol className="em-toc-list">
            <li><a href="#section-contacts">{c.contactsTitle}</a></li>
            <li><a href="#section-workhours">{c.workHoursTitle}</a></li>
            <li><a href="#section-cellphone">{c.cellTitle}</a></li>
            <li><a href="#section-dresscode">{c.hrTitle}</a></li>
            <li><a href="#section-holiday">{c.holidayTitle}</a></li>
            <li><a href="#section-pto">{c.ptoTitle}</a></li>
            <li><a href="#section-nylaw">{c.nyLawTitle}</a></li>
            <li><a href="#section-dbl">{c.dblTitle}</a></li>
            <li><a href="#section-pfl">{c.pflTitle}</a></li>
            <li><a href="#section-bereavement">{c.bereaveTitle}</a></li>
            <li><a href="#section-jury">{c.juryTitle}</a></li>
            <li><a href="#section-pay">{c.payTitle}</a></li>
            <li><a href="#section-antiharass">{c.antiHarassTitle}</a></li>
          </ol>
        </nav>

        {/* ── CONTACTS ── */}
        <section className="em-section" id="section-contacts">
          <button className="em-collapsible-title" onClick={() => setShowContacts(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.contactsTitle}</h2>
            <span className={`em-chevron${showContacts ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showContacts && (
            <div className="em-collapsible-body">
              <p className="em-p" style={{ fontStyle: 'italic', marginBottom: '16px' }}>{c.contactsNote}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {c.contacts.map((contact) => (
                  <div key={contact.phone} className="em-ceo-box" style={{ flex: '1', minWidth: '220px' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)' }}>
                      {contact.role}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '700', fontSize: '16px' }}>{contact.name}</p>
                    <a
                      href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--green-accent)', textDecoration: 'none', fontWeight: '600', fontSize: '15px' }}
                    >
                      {contact.phone}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        {/* ── WORK HOURS POLICY ── */}
        <section className="em-section" id="section-workhours">
          <button className="em-collapsible-title" onClick={() => setShowWorkHours(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.workHoursTitle}</h2>
            <span className={`em-chevron${showWorkHours ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showWorkHours && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.workHoursBody1}</p>
                <p className="em-p" style={{ margin: 0 }}>{c.workHoursBody2}</p>
              </div>
            </div>
          )}
        </section>

        {/* ── CELL PHONE POLICY ── */}
        <section className="em-section" id="section-cellphone">
          <button className="em-collapsible-title" onClick={() => setShowCellPhone(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.cellTitle}</h2>
            <span className={`em-chevron${showCellPhone ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showCellPhone && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.cellBody1}</p>
                <p className="em-p">{c.cellBody2}</p>
                <p className="em-p" style={{ margin: 0 }}>{c.cellBody3}</p>
              </div>
            </div>
          )}
        </section>

        {/* ── DRESS CODE POLICY ── */}
        <section className="em-section" id="section-dresscode">
          <button className="em-collapsible-title" onClick={() => setShowDressCode(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.hrTitle}</h2>
            <span className={`em-chevron${showDressCode ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showDressCode && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.hrIntro}</p>
                <ul className="em-ul">
                  {c.hrBullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                <p className="em-p" style={{ marginTop: '14px' }}>{c.hrHelp}</p>
                <p className="em-italic-close">{c.hrClose}</p>
              </div>
            </div>
          )}
        </section>

        {/* ── HOLIDAY POLICY ── */}
        <section className="em-section" id="section-holiday">
          <button className="em-collapsible-title" onClick={() => setShowHoliday(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.holidayTitle}</h2>
            <span className={`em-chevron${showHoliday ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showHoliday && (
            <div className="em-collapsible-body">
              <p className="em-p">{c.holidayIntro}</p>

              <h3 className="em-sub-title">{c.closedTitle}</h3>
              <div className="em-info-box">
                <p style={{ margin: 0 }}>{c.closedNote}</p>
              </div>

              <h3 className="em-sub-title">{c.federalTitle}</h3>
              <p className="em-p">{c.federalNote}</p>

              <h3 className="em-sub-title">{c.scheduleTitle}</h3>
              <div className="em-table-wrap">
                <table className="em-table">
                  <thead>
                    <tr>
                      <th>{c.colHoliday}</th>
                      <th>{c.colDate}</th>
                      <th>{c.colStatus}</th>
                      <th>{c.colPersonal}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedHolidays.map((key) => (
                      <tr key={key}>
                        <td>{c[key]}</td>
                        <td><span className="em-badge em-badge-tbd">{holidayDates[key]}</span></td>
                        <td><span className="em-badge em-badge-closed">{c.closed}</span></td>
                        <td>{c.no}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>{c.thanksgiving}</td>
                      <td><span className="em-badge em-badge-tbd">{holidayDates.thanksgiving}</span></td>
                      <td><span className="em-badge em-badge-open">{c.open}</span></td>
                      <td>{c.no}</td>
                    </tr>
                    <tr>
                      <td>{c.christmas}</td>
                      <td><span className="em-badge em-badge-tbd">{holidayDates.christmas}</span></td>
                      <td><span className="em-badge em-badge-open">{c.open}</span></td>
                      <td>{c.no}</td>
                    </tr>
                    <tr>
                      <td>{c.federalOther}</td>
                      <td>—</td>
                      <td><span className="em-badge em-badge-open">{c.open}</span></td>
                      <td>{c.yesIfTaken}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* ── PTO & SICK DAYS ── */}
        <section className="em-section" id="section-pto">
          <button className="em-collapsible-title" onClick={() => setShowPto(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.ptoTitle}</h2>
            <span className={`em-chevron${showPto ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showPto && (
            <div className="em-collapsible-body">
              <h3 className="em-sub-title">{c.ptoSubTitle}</h3>
              <ul className="em-ul">
                {c.ptoBullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>

              <h3 className="em-sub-title">{c.sickSubTitle}</h3>
              <ul className="em-ul">
                {c.sickBullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>

              <h3 className="em-sub-title">{c.deductTitle}</h3>
              <ul className="em-ul">
                {c.deductBullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
        </section>

        {/* ── NY STATE LAW REFERENCE ── */}
        <section className="em-section" id="section-nylaw">
          <button className="em-collapsible-title" onClick={() => setShowNyLaw(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.nyLawTitle}</h2>
            <span className={`em-chevron${showNyLaw ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showNyLaw && (
            <div className="em-collapsible-body">
              <p className="em-p">{c.nyLawIntro}</p>

              <h3 className="em-sub-title">{c.sickLawTitle}</h3>
              <p className="em-p">{c.sickLawIntro}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {c.sickLawItems.map((item, i) => (
                  <div key={i} className="em-ceo-box" style={{ padding: '14px 18px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: 'var(--text-primary)', fontSize: '14px' }}>{item.heading}</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="em-info-box" style={{ marginBottom: '12px' }}>
                <p style={{ margin: 0 }}>{c.sickPayNote}</p>
              </div>
              <p className="em-p" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                {c.sickLawSource.split(' — ')[0]} —{' '}
                <a href={c.sickLawSourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green-accent)' }}>
                  {c.sickLawSourceUrl}
                </a>
              </p>

              <h3 className="em-sub-title">{c.personalLawTitle}</h3>
              <p className="em-p">{c.personalLawIntro}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {c.personalLawItems.map((item, i) => (
                  <div key={i} className="em-info-box" style={{ padding: '14px 18px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: 'var(--text-primary)', fontSize: '14px' }}>{item.heading}</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="em-p em-italic-close">{c.nyLawClosing}</p>
            </div>
          )}
        </section>

        {/* ── EXTENDED ILLNESS / DBL ── */}
        <section className="em-section" id="section-dbl">
          <button className="em-collapsible-title" onClick={() => setShowDbl(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.dblTitle}</h2>
            <span className={`em-chevron${showDbl ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showDbl && (
            <div className="em-collapsible-body">
              <p className="em-p">{c.dblIntro}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {c.dblItems.map((item, i) => (
                  <div key={i} className="em-ceo-box" style={{ padding: '14px 18px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: 'var(--text-primary)', fontSize: '14px' }}>{item.heading}</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="em-p" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                {c.dblFormNote}{' '}
                <a href={c.dblFormUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green-accent)' }}>
                  {c.dblFormUrl}
                </a>
              </p>
            </div>
          )}
        </section>




        {/* ── PAID FAMILY LEAVE ── */}
        <section className="em-section" id="section-pfl">
          <button className="em-collapsible-title" onClick={() => setShowPfl(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.pflTitle}</h2>
            <span className={`em-chevron${showPfl ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showPfl && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.pflBody1}</p>
                <p className="em-p">{c.pflBody2}</p>
                <p className="em-p" style={{ fontStyle: 'italic' }}>
                  {c.pflNote}{' '}
                  <a
                    href={c.pflLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--green-accent)', wordBreak: 'break-all' }}
                  >
                    {c.pflLink}
                  </a>
                </p>
              </div>
            </div>
          )}
        </section>

        {/* ── BEREAVEMENT LEAVE ── */}
        <section className="em-section" id="section-bereavement">
          <button className="em-collapsible-title" onClick={() => setShowBereavement(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.bereaveTitle}</h2>
            <span className={`em-chevron${showBereavement ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showBereavement && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p" style={{ margin: 0 }}>{c.bereaveBody}</p>
              </div>
            </div>
          )}
        </section>

        {/* ── JURY DUTY ── */}
        <section className="em-section" id="section-jury">
          <button className="em-collapsible-title" onClick={() => setShowJury(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.juryTitle}</h2>
            <span className={`em-chevron${showJury ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showJury && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.juryBody1}</p>
                <p className="em-p" style={{ margin: 0 }}>{c.juryBody2}</p>
              </div>
              <p className="em-p" style={{ fontSize: '13px', fontStyle: 'italic', marginTop: '10px' }}>
                {c.jurySource} —{' '}
                <a href={c.jurySourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green-accent)' }}>
                  {c.jurySourceUrl}
                </a>
              </p>
            </div>
          )}
        </section>

        {/* ── PAY SCHEDULE ── */}
        <section className="em-section" id="section-pay">
          <button className="em-collapsible-title" onClick={() => setShowPay(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.payTitle}</h2>
            <span className={`em-chevron${showPay ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showPay && (
            <div className="em-collapsible-body">
              <div className="em-hr-box">
                <p className="em-p">{c.payBody1}</p>
                <p className="em-p">{c.payBody2}</p>
                <p className="em-p">
                  {c.payStubNote}{' '}
                  <a href={c.payStubUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green-accent)' }}>
                    {c.payStubUrl}
                  </a>
                </p>
                <p className="em-p" style={{ margin: 0 }}>{c.payHrNote}</p>
              </div>
            </div>
          )}
        </section>

        {/* ── ANTI-HARASSMENT ── */}
        <section className="em-section" id="section-antiharass">
          <button className="em-collapsible-title" onClick={() => setShowAntiHarass(v => !v)}>
            <h2 className="em-section-title" style={{ margin: 0 }}>{c.antiHarassTitle}</h2>
            <span className={`em-chevron${showAntiHarass ? ' em-chevron-open' : ''}`}>▸</span>
          </button>
          {showAntiHarass && (
            <div className="em-collapsible-body">
              <div className="em-ceo-box">
                <p className="em-p">{c.antiHarassBody}</p>
                <ul className="em-ul">
                  {c.antiHarassBullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                <p className="em-p" style={{ fontSize: '13px', fontStyle: 'italic', marginTop: '10px', marginBottom: 0 }}>
                  {c.antiHarassSource} —{' '}
                  <a href={c.antiHarassSourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green-accent)' }}>
                    {c.antiHarassSourceUrl}
                  </a>
                </p>
              </div>
            </div>
          )}
        </section>

        <div className="em-footer">
          <p>{c.footer}</p>
        </div>
      </div>

      {/* ── INTERNAL MEMORANDUM — Management Review Only ── */}
      <aside className="em-memo-sidebar">
        <div className="em-memo-header">
          <span className="em-memo-badge">⚠ INTERNAL ONLY</span>
          <h3 className="em-memo-title">Internal Memorandum</h3>
          <p className="em-memo-subtitle">Not for Distribution to Employees</p>
        </div>
        <div className="em-memo-body">
          <div className="em-memo-meta">
            <div className="em-memo-meta-row"><span>TO:</span> Management / Owner</div>
            <div className="em-memo-meta-row"><span>DATE:</span> May 2026</div>
            <div className="em-memo-meta-row"><span>RE:</span> Sick Leave Compliance</div>
          </div>
          <div className="em-memo-alert">
            <strong>⚠ Review Before Publishing</strong>
          </div>
          <p className="em-memo-p">
            <strong>Issue 1 — Auto Cash-Out of Unused Sick Days:</strong><br />
            The original policy paid out unused sick days in the annual bonus check.
            NY Labor Law § 196-b <em>requires</em> unused sick days to carry over — they cannot
            be forfeited or automatically cashed out without the employee's request.
          </p>
          <p className="em-memo-p">
            <strong>Issue 2 — The "12-Day Deduction" Rule:</strong><br />
            The original policy offered 12 flat sick days (non-accruing) and reduced sick day
            pay when an employee's total absences exceeded 12. Two problems here:
          </p>
          <ul className="em-memo-ul" style={{ marginBottom: '10px' }}>
            <li>
              <strong>Non-accruing structure:</strong> NY Labor Law § 196-b requires that
              the legally-mandated 5 paid sick days accrue from the employee's date of hire.
              A flat non-accruing grant does not satisfy this requirement.
            </li>
            <li>
              <strong>Pay deduction as penalty:</strong> Reducing sick day pay because an
              employee exceeded an absence threshold is illegal. § 196-b prohibits any
              financial penalty for using lawfully accrued sick leave. Note: this is
              specifically about <em>pay deductions</em> — standard attendance discipline
              (verbal warnings, write-ups, termination for excessive absences beyond
              protected leave) remains a legal and valid management tool.
            </li>
          </ul>
          <p className="em-memo-p">
            <strong>Legal Risk (both issues):</strong><br />
            Non-compliance exposes DiNi Wigs to NYSDOL complaints, back-pay claims, audits,
            and civil penalties. The deduction rule in particular carries significant retaliation
            liability under § 196-b(4)(c).
          </p>
          <p className="em-memo-p"><strong>What Was Updated in This Manual:</strong></p>
          <ul className="em-memo-ul">
            <li>The 12-day deduction rule has been removed entirely</li>
            <li>Unused sick days now carry over year to year (required by law)</li>
            <li>Employees may voluntarily request a cash-out at year end</li>
            <li>Sick days used are paid on the following pay cycle</li>
            <li>Annual usage cap remains 5 paid sick days / year</li>
          </ul>
          <p className="em-memo-p" style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
            Please review the sick day language in Sections 6 and 7 before approving this
            manual for distribution to employees.
          </p>
          <button
            onClick={handlePrintMemo}
            style={{
              marginTop: '14px',
              width: '100%',
              padding: '8px 0',
              background: 'none',
              border: '1px solid #f59e0b',
              borderRadius: '5px',
              color: '#f59e0b',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              letterSpacing: '0.4px',
            }}
          >
            Print / Save Memo as PDF
          </button>
        </div>
      </aside>

      </div>
    </div>
  );
};

export default EmployeeManual;
